import React, { useCallback, useContext, useEffect, useState } from 'react';
import { MdDelete } from "react-icons/md";
import SummaryApi from '../common';
import Context from '../context';
import displayINRCurrency from '../helpers/displayCurrency';
import Paypal from '../common/Paypal';

const Cart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckOut] = useState(false);
  const context = useContext(Context);
  const loadingCart = new Array(4).fill(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = SummaryApi.addToCartProductView.url;

      if (token) {
        url += `?token=${token}`;
      }

      const response = await fetch(url, {
        method: SummaryApi.addToCartProductView.method,
        credentials: 'include',
        headers: {
          "content-type": 'application/json'
        },
      });

      const responseData = await response.json();

      if (responseData.success) {
        setData(responseData.data || []);
      }
    } catch (error) {
      console.error("Error fetching cart data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const increaseQty = async (id, qty) => {
    try {
      const token = localStorage.getItem("token");
      let url = SummaryApi.updateCartProduct.url;

      if (token) {
        url += `?token=${token}`;
      }

      const response = await fetch(url, {
        method: SummaryApi.updateCartProduct.method,
        credentials: 'include',
        headers: {
          "content-type": 'application/json'
        },
        body: JSON.stringify({
          _id: id,
          quantity: qty + 1
        })
      });

      const responseData = await response.json();

      if (responseData.success) {
        fetchData();
      }
    } catch (error) {
      console.error("Error increasing quantity:", error);
    }
  };

  const decreaseQty = async (id, qty) => {
    if (qty >= 2) {
      try {
        const token = localStorage.getItem("token");
        let url = SummaryApi.updateCartProduct.url;

        if (token) {
          url += `?token=${token}`;
        }

        const response = await fetch(url, {
          method: SummaryApi.updateCartProduct.method,
          credentials: 'include',
          headers: {
            "content-type": 'application/json'
          },
          body: JSON.stringify({
            _id: id,
            quantity: qty - 1
          })
        });

        const responseData = await response.json();

        if (responseData.success) {
          fetchData();
        }
      } catch (error) {
        console.error("Error decreasing quantity:", error);
      }
    }
  };

  const deleteCartProduct = async (id) => {
    try {
      const token = localStorage.getItem("token");
      let url = SummaryApi.deleteCartProduct.url;

      if (token) {
        url += `?token=${token}`;
      }

      const response = await fetch(url, {
        method: SummaryApi.deleteCartProduct.method,
        credentials: 'include',
        headers: {
          "content-type": 'application/json'
        },
        body: JSON.stringify({
          _id: id,
        })
      });

      const responseData = await response.json();

      if (responseData.success) {
        fetchData();
        context.fetchUserAddToCart();
      }
    } catch (error) {
      console.error("Error deleting cart product:", error);
    }
  };

  const totalQty = data.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0);
  const totalPrice = data.reduce((prev, curr) => prev + (curr.quantity * curr?.productId?.sellingPrice), 0);

  const orderData = data.map(product => ({
    name: product?.productId?.productName,
    price: product?.productId?.sellingPrice,
    quantity: product?.quantity,
  }));

  return (
    <div className='container mx-auto'>
      <div className='text-center text-lg my-3'>
        {data?.length === 0 && !loading && (
          <p className='bg-white py-5 rounded shadow'>Your cart is empty</p>
        )}
      </div>

      <div className='flex flex-col lg:flex-row gap-10 lg:justify-between p-4'>
        {/* View products */}
        <div className='w-full max-w-3xl'>
          {loading ? (
            loadingCart.map((_, index) => (
              <div
                key={`loading-${index}`}
                className='w-full bg-slate-200 h-32 my-2 border border-slate-300 animate-pulse rounded'
              >
                <span className='sr-only'>Loading cart item {index + 1}</span>
              </div>
            ))
          ) : (
            data.map((product) => (
              <div
                key={product?._id}
                className='w-full bg-white h-32 my-2 border border-slate-300 rounded grid grid-cols-[128px,1fr]'
              >
                <div className='w-32 h-32 bg-slate-200'>
                  <img
                    src={product?.productId?.productImage[0]}
                    alt={product?.productId?.productName}
                    className='w-full h-full object-scale-down mix-blend-multiply'
                  />
                </div>
                <div className='px-4 py-2 relative'>
                  {/* Delete product */}
                  <button
                    className='absolute right-0 text-green-600 rounded-full p-2 hover:bg-green-600 hover:text-white cursor-pointer transition-colors'
                    onClick={() => deleteCartProduct(product?._id)}
                    aria-label={`Remove ${product?.productId?.productName} from cart`}
                  >
                    <MdDelete />
                  </button>

                  <h2 className='text-lg lg:text-xl text-ellipsis line-clamp-1'>
                    {product?.productId?.productName}
                  </h2>
                  <p className='capitalize text-slate-500'>
                    {product?.productId?.category}
                  </p>
                  <div className='flex items-center justify-between'>
                    <p className='text-green-600 font-medium text-lg'>
                      {displayINRCurrency(product?.productId?.sellingPrice)}
                    </p>
                    <p className='text-slate-600 font-semibold text-lg'>
                      {displayINRCurrency(product?.productId?.sellingPrice * product?.quantity)}
                    </p>
                  </div>
                  <div className='flex items-center gap-3 mt-1'>
                    <button
                      className='border border-green-600 text-green-600 hover:bg-green-600 hover:text-white w-6 h-6 flex justify-center items-center rounded transition-colors'
                      onClick={() => decreaseQty(product?._id, product?.quantity)}
                      aria-label="Decrease quantity"
                      disabled={product?.quantity <= 1}
                    >
                      -
                    </button>
                    <span>{product?.quantity}</span>
                    <button
                      className='border border-green-600 text-green-600 hover:bg-green-600 hover:text-white w-6 h-6 flex justify-center items-center rounded transition-colors'
                      onClick={() => increaseQty(product?._id, product?.quantity)}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        <div className='mt-5 lg:mt-0 w-full max-w-sm'>
          {loading ? (
            <div className='h-36 bg-slate-200 border border-slate-300 animate-pulse rounded'>
              <span className='sr-only'>Loading summary</span>
            </div>
          ) : (
            <div className='bg-white rounded shadow'>
              <h2 className='text-white bg-green-600 px-4 py-1 rounded-t'>Summary</h2>
              <div className='p-4'>
                <div className='flex items-center justify-between gap-2 font-medium text-lg text-slate-600 mb-2'>
                  <p>Quantity</p>
                  <p>{totalQty}</p>
                </div>

                <div className='flex items-center justify-between gap-2 font-medium text-lg text-slate-600 mb-4'>
                  <p>Total Price</p>
                  <p>{displayINRCurrency(totalPrice)}</p>
                </div>

                {checkout ? (
                  <Paypal orderData={orderData} totalAmount={totalPrice} />
                ) : (
                  <button
                    className='bg-blue-600 p-2 text-white w-full rounded hover:bg-blue-700 transition-colors'
                    onClick={() => setCheckOut(true)}
                    disabled={data.length === 0}
                  >
                    Checkout
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;